import Dispatcher from '../core/Dispatcher';
import {Constants} from '../constants';
import {arrayOf, normalize, Schema} from 'normalizr';
import _ from 'lodash';
import PriceAlertsAction from '../actions/PriceAlertsAction';

//Token
let jwt = localStorage.getItem('jwt');

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize price alert data using normalizr schema
 * @param {object} data - Raw API response
 * @returns {object} Normalized data with entities
 */
var formalizeData = (data) => {
  const priceAlert = new Schema('priceAlerts');
  const value = new Schema('values');
  const supplier = new Schema('suppliers');
  const airport = new Schema('airports', {idAttribute: 'icao'});
  const platt = new Schema('platts');
  const contract = new Schema('contracts');
  const user = new Schema('users');
  const history = new Schema('priceAlertHistory');

  priceAlert.define({
    value: value,
    previousValue: value,
    Supplier: supplier,
    Airport: airport,
    Platt: platt,
    Contract: contract,
    resolver: user,
    history: arrayOf(history)
  });

  history.define({
    user: user
  });

  // Handle both single alert and array of alerts
  if (data.alerts) {
    return normalize(data.alerts, arrayOf(priceAlert));
  } else if (data.alert) {
    return normalize(data.alert, priceAlert);
  }
  
  return { entities: { priceAlerts: {} }, result: [] };
}

/**
 * Generic authenticated AJAX request with dispatcher integration
 * @param {object} config - Request configuration
 * @param {string} config.url - API endpoint
 * @param {string} config.type - HTTP method (GET, POST, DELETE)
 * @param {object} [config.data] - Request payload
 * @param {boolean} [config.contentType] - Content type header
 * @param {string} config.successAction - Action type for success
 * @param {string} config.errorAction - Action type for error
 * @param {function} [config.transformResponse] - Transform response before dispatching
 * @param {function} [config.onSuccess] - Callback after successful dispatch
 * @returns {void}
 */
var makeAuthenticatedRequest = (config) => {
  const ajaxConfig = {
    url: config.url,
    type: config.type,
    beforeSend: (req) => {
      req.setRequestHeader("Authorization", "bearer " + localStorage.getItem('jwt'));
    }
  };

  if (config.contentType) {
    ajaxConfig.contentType = config.contentType;
  }

  if (config.data) {
    ajaxConfig.data = config.data;
  }

  Promise.resolve($.ajax(ajaxConfig))
    .then(function (data) {
      const payload = config.transformResponse ? config.transformResponse(data) : data;
      
      Dispatcher.handleViewAction({
        actionType: config.successAction,
        ...payload
      });
      
      // Call onSuccess callback if provided
      if (config.onSuccess) {
        config.onSuccess(data);
      }
    })
    .catch(function (err) {
      Dispatcher.handleViewAction({
        actionType: config.errorAction,
        err: err
      });
    });
}

// ============================================================================
// API METHODS
// ============================================================================

const PriceAlertsApiUtils = {
  /**
   * Get all alerts with optional filters
   */
  getAll: function (filters = {}) {
    const params = new URLSearchParams(filters).toString();
    const url = '/api/price-alerts' + (params ? '?' + params : '');

    makeAuthenticatedRequest({
      url: url,
      type: 'GET',
      successAction: Constants.ActionPriceAlerts.LOAD_PRICE_ALERTS_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.LOAD_PRICE_ALERTS_ERROR,
      transformResponse: (data) => ({ data: formalizeData(data) })
    });
  },

  /**
   * Get single alert by ID
   */
  getOne: function (id) {
    makeAuthenticatedRequest({
      url: '/api/price-alerts/' + id,
      type: 'GET',
      successAction: Constants.ActionPriceAlerts.LOAD_PRICE_ALERT_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.LOAD_PRICE_ALERT_ERROR,
      transformResponse: (data) => ({ data: formalizeData(data) })
    });
  },

  /**
   * Skip an alert (user accepted price change)
   */
  skipAlert: function (alertId) {
    makeAuthenticatedRequest({
      url: '/api/price-alerts/' + alertId + '/skip',
      type: 'POST',
      successAction: Constants.ActionPriceAlerts.SKIP_PRICE_ALERT_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.SKIP_PRICE_ALERT_ERROR,
      transformResponse: (data) => ({ data: formalizeData(data) }),
      onSuccess: () => {
        // Refresh alert count after skipping alert
        PriceAlertsAction.getNewAlertsCount();
      }
    });
  },

  /**
   * Bulk skip multiple alerts at once
   */
  bulkSkipAlerts: function (alertIds) {
    makeAuthenticatedRequest({
      url: '/api/price-alerts/bulk-skip',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({ alertIds }),
      successAction: Constants.ActionPriceAlerts.BULK_SKIP_PRICE_ALERTS_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.BULK_SKIP_PRICE_ALERTS_ERROR,
      transformResponse: (data) => ({ data: formalizeData(data) }),
      onSuccess: () => {
        // Refresh alert count after batch skipping
        PriceAlertsAction.getNewAlertsCount();
      }
    });
  },

  /**
   * Resolve alert with new price value
   */
  resolveAlert: function (alertId, newValue) {
    makeAuthenticatedRequest({
      url: '/api/price-alerts/' + alertId + '/resolve',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({ newValue }),
      successAction: Constants.ActionPriceAlerts.RESOLVE_PRICE_ALERT_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.RESOLVE_PRICE_ALERT_ERROR,
      transformResponse: (data) => ({ data: formalizeData(data) }),
      onSuccess: () => {
        // Refresh alert count after resolving alert
        PriceAlertsAction.getNewAlertsCount();
      }
    });
  },

  /**
   * Delete an alert
   */
  deleteAlert: function (alertId) {
    makeAuthenticatedRequest({
      url: '/api/price-alerts/' + alertId,
      type: 'DELETE',
      successAction: Constants.ActionPriceAlerts.DELETE_PRICE_ALERT_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.DELETE_PRICE_ALERT_ERROR,
      transformResponse: () => ({ alertId: alertId }),
      onSuccess: () => {
        // Refresh alert count after deleting alert
        PriceAlertsAction.getNewAlertsCount();
      }
    });
  },

  /**
   * Get count of new alerts
   */
  getNewAlertsCount: function () {
    makeAuthenticatedRequest({
      url: '/api/price-alerts/count/new',
      type: 'GET',
      successAction: Constants.ActionPriceAlerts.LOAD_NEW_ALERTS_COUNT_SUCCESS,
      errorAction: Constants.ActionPriceAlerts.LOAD_NEW_ALERTS_COUNT_ERROR,
      transformResponse: (data) => ({ count: data.count || 0 })
    });
  }
};

export default PriceAlertsApiUtils;
